/**
 * User journey for /features — ordered path through the product.
 * Keep in sync with Study hub flow (Discover → Read → Save → Practice → Review) plus Learn & Teams.
 */

export const FEATURE_JOURNEY_INTRO = {
  title: 'Your learning journey',
  lead:
    'Follow the same path you take after sign-in: build material in Notebook hub, find Q&A in Library, then live on Study hub with practice, spaced review, AI, and optional Teams.',
  loopLabel: 'Discover → Read → Save → Practice → Review',
};

export const FEATURE_JOURNEY_STEPS = [
  {
    id: 'start',
    step: 0,
    phase: 'Start',
    title: 'Sign in & personalize',
    hub: 'Account',
    path: '/login',
    pathLabel: 'Sign in',
    summary:
      'Create a free account (Google or email), pick topics, and set a daily review goal. Optional MFA when you are ready.',
    features: [
      'HttpOnly session cookies — no tokens in localStorage',
      'Onboarding tour: modules, goals, first notebook nudge',
      'Settings for learning interests & notifications',
      'Optional TOTP two-factor authentication',
    ],
    planNote: 'Included on Free, Pro, and Team',
  },
  {
    id: 'notebook-hub',
    step: 1,
    phase: 'Build',
    title: 'Notebook hub — create & import',
    hub: 'Learn',
    path: '/hub',
    pathLabel: 'Open Notebook hub',
    summary:
      'One notebook per course, exam, or project. Import PDFs (including scanned pages with OCR), URLs, or paste text — AI generates structured Q&A for you to study.',
    features: [
      'Create private notebooks & organize by subject',
      'PDF import with background jobs, progress & review queue',
      'OCR for scanned PDFs when Tesseract is available',
      'Public catalog — browse and subscribe to shared notebooks',
      'Import history & confirm generated questions before publish',
    ],
    planNote: 'Notebook hub on all plans',
  },
  {
    id: 'discover',
    step: 2,
    phase: 'Discover',
    title: 'Find what to study next',
    hub: 'Study hub',
    path: '/dashboard',
    pathLabel: 'Study hub',
    summary:
      'Recommendations, trending notebooks, continue reading, and revision due — one dashboard so you always know the next best action.',
    features: [
      'Continue where you left off & personalized recommendations',
      'Trending & catalog highlights for your modules',
      'Due review count, streaks, and daily goal progress',
      'Gamification: XP, achievements, optional leaderboard (Pro+)',
    ],
    planNote: 'Dashboard & basic analytics on Free',
  },
  {
    id: 'read',
    step: 3,
    phase: 'Read',
    title: 'Library — search & read Q&A',
    hub: 'Library',
    path: '/library',
    pathLabel: 'Open Library',
    summary:
      'Server-side search across your modules, subscribed notebooks, and imports — filters for notebook, topic, level, and read history with infinite scroll.',
    features: [
      'Paginated search API with rich filters',
      'Read list & question detail with images when present',
      'Questions from chosen modules + notebook subscriptions',
      'Preview mode for catalog content before you subscribe',
    ],
    planNote: 'Full library search on Free',
  },
  {
    id: 'save',
    step: 4,
    phase: 'Save',
    title: 'Bookmark & collections',
    hub: 'Saved',
    path: '/saved',
    pathLabel: 'Saved questions',
    summary:
      'Save tricky cards while you read. Practice later from your saved deck or mix saved items with library filters.',
    features: [
      'One-click save from library & notebook study views',
      'Saved deck count on Study hub & nav',
      'Practice session scoped to saved items only',
    ],
    planNote: 'Save & collections on Free',
  },
  {
    id: 'practice',
    step: 5,
    phase: 'Practice',
    title: 'Practice mode & flashcards',
    hub: 'Practice',
    path: '/practice',
    pathLabel: 'Start practice',
    summary:
      'Active recall without scheduling pressure — flip cards, reveal answers, and mark confidence before you enter the spaced repetition queue.',
    features: [
      'Flashcard UI with Again / Hard / Good / Easy',
      'Practice from saved deck or filtered library sets',
      'Adaptive “next best question” hints on Study hub',
    ],
    planNote: 'Practice on Free',
  },
  {
    id: 'review',
    step: 6,
    phase: 'Review',
    title: 'Spaced repetition queue',
    hub: 'Review',
    path: '/review',
    pathLabel: 'Open review queue',
    summary:
      'The core retention loop: items return on a schedule based on how well you remembered them. Clear due items from Study hub or the dedicated review page.',
    features: [
      'SM-2 style scheduling from your ratings',
      'Due count surfaced on dashboard & notifications',
      'Review session focused on what is due today',
    ],
    planNote: 'Spaced repetition on Free',
  },
  {
    id: 'ai',
    step: 7,
    phase: 'Understand',
    title: 'AI explain & discovery',
    hub: 'In context',
    path: '/ai',
    pathLabel: 'AI on QnaRead',
    summary:
      'Server-side AI on the question you are studying — not a generic chat tab. Pro adds mind-map search and related-topic discovery.',
    features: [
      'In-depth explain on any question (daily quota by plan)',
      'Mind map AI search & related topics (Pro / Team)',
      'Metered limits shown on Study hub & Settings',
    ],
    planNote: 'AI explain on Free; higher limits & mind map on Pro+',
  },
  {
    id: 'analytics',
    step: 8,
    phase: 'Measure',
    title: 'Progress, analytics & export',
    hub: 'Profile',
    path: '/profile',
    pathLabel: 'My progress',
    summary:
      'Charts, streaks, activity history, and JSON export when you need to backup or analyze outside the app.',
    features: [
      'Learning progress panel & study analytics on dashboard',
      'Profile stats, achievements, and rank (Pro+ for leaderboard)',
      'Advanced analytics & JSON export (Pro / Team)',
    ],
    planNote: 'Basic analytics Free · Advanced & export Pro+',
  },
  {
    id: 'share',
    step: 9,
    phase: 'Share',
    title: 'Notebook sharing (Pro)',
    hub: 'Learn',
    path: '/hub',
    pathLabel: 'Share notebooks',
    summary:
      'Share notebooks with your team and invite collaborators by email — without moving everyone into a full Team workspace.',
    features: [
      'Share notebook with team (Pro / Team)',
      'Email invite collaborators to a notebook',
      'Private notebooks by default',
    ],
    planNote: 'Pro & Team',
  },
  {
    id: 'teams',
    step: 10,
    phase: 'Collaborate',
    title: 'Team workspaces',
    hub: 'Teams',
    path: '/teams',
    pathLabel: 'Teams home',
    summary:
      'Organizations for cohorts and companies: invites, roles, assignments, challenges, team leaderboard, manager views, and audit-friendly settings.',
    features: [
      'Create org, invite members, roles (owner → viewer)',
      'Assignments & challenges with team notifications',
      'Team leaderboard, learning score & manager dashboard',
      'Groups, audit log & leaderboard privacy controls',
    ],
    planNote: 'Team plan · trial workspaces available',
  },
  {
    id: 'trust',
    step: 11,
    phase: 'Trust',
    title: 'Billing, security & admin',
    hub: 'Platform',
    path: '/security',
    pathLabel: 'Security',
    summary:
      'Stripe checkout for Pro, billing portal, role-based admin APIs, and operator tools for content teams.',
    features: [
      'Stripe Pro checkout & customer portal',
      'Role-based admin console & question editor (scoped)',
      'Bulk upload & notebook restrictions for operators',
      'Privacy, cookies, and export/delete via support',
    ],
    planNote: 'Free to start · Pro via Stripe',
  },
];
